import React from 'react';
import {
	Route,
	Routes,
	useLocation,
	useParams,
} from 'react-router-dom';

import loadable from '@common/react/loadable/loadableSettings';

import BounceLoader from '@common/react/components/UI/Loaders/BounceLoader';
import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import listenerForBrowser from '@app/components/Pages/listenerForBrowser';
import MobileMenuSlideInMenu from '@app/components/UI/MobileSlideInMenu/MobileSlideInMenu';
import Masthead from '@app/components/UI/MastHead/Masthead';
import MastheadBadge from '@app/components/UI/MastHead/MastheadBadge';
import MastheadNaked from '@app/components/UI/MastHead/MastheadNaked';
import SidebarWithCaptions from '@app/components/UI/SidebarWithCaptions/SidebarWithCaptions';
import { ContentCategories } from '@app/objects/ContentCategories';
import { ContentCategory } from '@app/objects/ContentCategory';
import { Issues } from '@app/objects/Questions';
import { ResourceTopic } from '@app/objects/ResourceTopic';
import '@app/scss/components/blog.scss';
import { useIsMobile } from '@app/hooks/useIsMobile';

const ResourcesCenterPage = loadable(() => import(/* webpackChunkName: "ResourcesPage" */ '@app/components/Pages/Resources/FamilyResources/ResourcesCenterPage'));
const TopicPage = loadable(() => import(/* webpackChunkName: "ResourceCenter" */ '@app/components/Pages/Resources/FamilyResources/TopicPage'));
const ResourcePage = loadable(() => import(/* webpackChunkName: "CatsAnxiety" */ '@app/components/Pages/Resources/FamilyResources/ResourcePage'));
const FRBlog = loadable(() => import(/* webpackChunkName: "FRBlog" */ '@app/components/Pages/Resources/FamilyResources/FRBlog'));
const FRVideos = loadable(() => import(/* webpackChunkName: "FRVideos" */ '@app/components/Pages/Resources/FamilyResources/FRVideos'));
const FRFaq = loadable(() => import(/* webpackChunkName: "FRFaq" */ '@app/components/Pages/Resources/FamilyResources/FRFaq'));

const FamilyResources: React.FC = () => {
	const params = useParams<{ topic?: string; id?: string; resource?: string }>();
	const location = useLocation();
	const [currentTopic, setCurrentTopic] = React.useState<ResourceTopic>();
	const [topics, setTopics] = React.useState<Array<ResourceTopic>>([]);
	const navCollapse = useIsMobile();
	const [currentCategoryBlog, setCurrentCategoryBlog] = React.useState<ContentCategory>();
	const [categoriesBlog, setCategoriesBlog] = React.useState<Array<ContentCategory>>();

	const [categoriesFAQ, setCategoriesFAQ] = React.useState<Array<Issues>>([]);
	const [currentCategoryFAQ, setCurrentCategoryFAQ] = React.useState<Issues>();
	const [currentCategorySimple, setCurrentCategorySimple] = React.useState();
	const [searchQuery, setSearchQuery] = React.useState();

	const [searchFieldValue, setSearchFieldValue] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const onSetCategory = (category) => {
		if (category.url === 'blog') {
			setCurrentCategoryBlog({
				id: 0,
				name: '',
				topics: [],
				url: '',
			});
		} else {
			setCurrentCategoryBlog(category);
		}
	};
	const onSetCategoryFaq = (category) => {
		if (category.name === 'FAQ') {
			setCurrentCategoryFAQ({
				name: '',
				questions: [],
			});
		} else {
			setCurrentCategoryFAQ(category);
		}
	};

	const onSetCategorySimple = (category) => {
		setCurrentCategorySimple(category.url);
	};

	const onSearchSubmit = (value) => {
		setSearchQuery(value);
	};

	const CategoryHandlers = {
		HowIKnow: onSetCategorySimple,
		Calendar: onSetCategorySimple,
		Blog: onSetCategory,
		FAQ: onSetCategoryFaq,
		Videos: onSetCategory,
		Common: onSetCategory,
		Newsletter: onSetCategory,
	};

	const imageAlts = {
		'common-diseases-in-cats': 'Common diseases in cats',
		'common-diseases-in-dogs': 'Common diseases in dogs',
		'symptom-management': 'Symptom management in pets',
	};

	React.useEffect(() => {
		rest.get<List<ResourceTopic>>('v1/resourceTopics')
			.then((res) => {
				setTopics(res.list);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, []);

	React.useEffect(() => {
		if (topics && params.topic) {
			setIsLoading(true);
			onSearchSubmit('');

			rest.get<ResourceTopic>(`v1/resourceTopics/${params.topic}`)
				.then((res) => {
					setCurrentTopic(res);
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}, [topics, params.topic]);

	// меню -- категории в блоге
	React.useEffect(() => {
		rest.get<ContentCategories>('v2/contentTopics/blog')
			.then((res) => {
				setCategoriesBlog(res.list);
			});
	}, []);

	React.useEffect(() => {
		if (location.pathname === '/blog') {
			onSetCategory({ url: 'blog' });
		}
	}, [location]);

	return (
		!isLoading ? (
			<div>
				{location.pathname === `/resource-center/${params.topic}` ? (
					<MastheadBadge
						title={currentTopic?.title ?? "Determining your pet's quality of life"}
						readMorePath="/how-will-i-know-it-is-time"
						readMoreTitle="Read more about Quality of Life"
						imageSrc={currentTopic?.avatar ?? require('@app/content/bulldog.jpg')}
						imageAlt={currentTopic ? imageAlts[currentTopic.url] : ''}
					/>
				) : location.pathname === `/faq/${params.id}` || location.pathname === '/faq' ? (
					<MastheadNaked imageSrc={require('@app/images/blog-bg.jpg')} title="Frequently Asked Questions" />
				) : location.pathname === '/resource-center' ? (
					<Masthead
						imageSrc={require('@app/content/Resource-Center.jpg')}
						title="Educational Resources"
						imageAlt="Senior pet resources"
					/>
				) : location.pathname === '/videos' ? (
					<MastheadNaked imageSrc={require('@app/images/blog-bg.jpg')} title="Lap of Love Videos" />
				) : location.pathname === `/resource-center/${params.topic}/${params.resource}` ? (
					null
				) : (
					<MastheadNaked imageSrc={require('@app/images/blog-bg.jpg')} title="Senior Pet Care Blog" />
				)}

				<div className="blog-page">
					<div className={`${location.pathname !== `/resource-center/${params.topic}/${params.resource}` ? 'blog-page-body' : ''}`}>
						{location.pathname !== `/resource-center/${params.topic}/${params.resource}` && (
							<>
								<MobileMenuSlideInMenu
									topics={topics}
									onSetIssue={CategoryHandlers}
									topicsInner={{ Blog: categoriesBlog, FAQ: categoriesFAQ }}
									typeTopicsInner={location.pathname.split('/')[1]}
									onSubmit={onSearchSubmit}
									searchFieldValue={searchFieldValue}
									setSearchFieldValue={setSearchFieldValue}
								/>
								<div className="blog-page__sidebar">
									<SidebarWithCaptions
										onSetIssue={CategoryHandlers}
										navCollapse={navCollapse}
										topics={topics}
										withSubmit={false}
										searchFieldName="searchFieldName"
										searchFieldPlaceHolder="Search by topic"
										topicsInner={{ Blog: categoriesBlog, FAQ: categoriesFAQ }}
										typeTopicsInner={location.pathname.split('/')[1]}
										onSubmit={onSearchSubmit}
										searchFieldValue={searchFieldValue}
										setSearchFieldValue={setSearchFieldValue}
									/>
								</div>
							</>
						)}

						<Routes>
							<Route path="/resource-center" element={<ResourcesCenterPage topics={topics} />} />
							<Route path="/resource-center/:topic" element={<TopicPage searchQuery={searchQuery} currentTopic={currentTopic} />} />
							<Route path="/resource-center/:topic/:resource" element={<ResourcePage currentTopic={currentTopic} />} />
							<Route
								path="/blog/:id?"
								element={(
									<FRBlog
										currentCategoryBlog={currentCategoryBlog as any}
										categoriesBlog={categoriesBlog}
										onSetCategory={onSetCategory}
										searchQuery={searchQuery}
									/>
								)}
							/>
							<Route path="/videos" element={<FRVideos searchQuery={searchQuery} />} />
							<Route
								path="/faq/:id?"
								element={(
									<FRFaq
										setCategoriesFAQ={setCategoriesFAQ}
										currentCategoryFAQ={currentCategoryFAQ}
										setCurrentCategoryFAQ={setCurrentCategoryFAQ}
										searchQuery={searchQuery}
									/>
								)}
							/>
						</Routes>
					</div>
				</div>
			</div>
		) : (
			<BounceLoader />
		)
	);
};

export default FamilyResources;
